import * as React from "react"
import { X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import type { GapClientConfig, UpdateGapClientRequest } from "@/types/gap-client"
import { useNumericInput, type NumericInputValue } from "@/hooks/useNumericInput"

interface ClientEditPanelProps {
  client: GapClientConfig | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (clientId: string, data: UpdateGapClientRequest) => Promise<void>
  isSaving?: boolean
}

interface FormData {
  venueId: NumericInputValue
  necThreshold: NumericInputValue
  userInstructionsEnabled: boolean
  adminPassword: string
}

export function ClientEditPanel({ client, open, onOpenChange, onSave, isSaving = false }: ClientEditPanelProps) {
  const [formData, setFormData] = React.useState<FormData>({
    venueId: "",
    necThreshold: "",
    userInstructionsEnabled: false,
    adminPassword: "",
  })
  const [originalData, setOriginalData] = React.useState<{
    venueId: number
    necThreshold: number
    userInstructionsEnabled: boolean
  } | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const firstInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (client) {
      const venueId = client.venueId
      const necThreshold = Math.round(client.necMatchScoreThreshold)
      const initialData: FormData = {
        venueId,
        necThreshold,
        userInstructionsEnabled: client.featureFlagUserInstructions,
        adminPassword: "",
      }
      setFormData(initialData)
      setOriginalData({
        venueId,
        necThreshold,
        userInstructionsEnabled: client.featureFlagUserInstructions,
      })
      setErrors({})
    }
  }, [client])

  React.useEffect(() => {
    if (open && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open])

  const venueIdInput = useNumericInput({
    min: 1,
    integer: true,
    value: formData.venueId,
    validate: (value) => {
      if (value === "") return "Venue ID must be a valid positive integer"
      if (!Number.isInteger(value) || value < 1) {
        return "Venue ID must be a positive integer"
      }
      return undefined
    },
  })

  const necThresholdInput = useNumericInput({
    min: 0,
    max: 10000,
    integer: true,
    value: formData.necThreshold,
    validate: (value) => {
      if (value === "") return "NEC Match Threshold must be a valid number between 0 and 10000"
      if (!Number.isInteger(value) || value < 0 || value > 10000) {
        return "NEC Match Threshold must be a valid number between 0 and 10000"
      }
      return undefined
    },
  })

  const hasChanges = React.useMemo(() => {
    if (!originalData) return false
    if (formData.venueId === "" || formData.necThreshold === "") return true
    return (
      formData.venueId !== originalData.venueId ||
      formData.necThreshold !== originalData.necThreshold ||
      formData.userInstructionsEnabled !== originalData.userInstructionsEnabled ||
      formData.adminPassword.length > 0
    )
  }, [formData, originalData])

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmDialog(true)
    } else {
      onOpenChange(false)
    }
  }

  const handleDiscard = () => {
    if (originalData) {
      setFormData({ ...originalData, adminPassword: "" })
    }
    setShowConfirmDialog(false)
    onOpenChange(false)
  }

  const handleSave = async () => {
    if (!client) return
    if (
      venueIdInput.error ||
      necThresholdInput.error ||
      formData.venueId === "" ||
      formData.necThreshold === ""
    ) {
      return
    }

    const updateData: UpdateGapClientRequest = {
      venueId: formData.venueId as number,
      necMatchScoreThreshold: Math.round(formData.necThreshold as number),
      featureFlagUserInstructions: formData.userInstructionsEnabled,
    }

    // Only include adminPassword if user entered a new value
    if (formData.adminPassword.trim()) {
      updateData.adminPassword = formData.adminPassword
    }

    await onSave(client.clientId, updateData)
    if (typeof formData.venueId === "number" && typeof formData.necThreshold === "number") {
      setOriginalData({
        venueId: formData.venueId,
        necThreshold: formData.necThreshold,
        userInstructionsEnabled: formData.userInstructionsEnabled,
      })
    }
    setFormData((prev) => ({ ...prev, adminPassword: "" }))
  }

  if (!client) return null

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="w-full sm:w-[540px] sm:max-w-[540px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
        >
          <SheetHeader className="px-6 py-5 border-b border-border">
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-lg font-semibold text-foreground">Edit Client</SheetTitle>
                <SheetDescription className="text-muted-foreground mt-1">
                  Update client configuration for <span className="font-mono">{client.clientId}</span>
                </SheetDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Client Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Client Information
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">Client ID</Label>
                <Input value={client.clientId} disabled className="bg-muted/50 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Client ID cannot be changed</p>
              </div>
            </div>

            {/* Configuration Settings */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Configuration Settings
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">
                  Venue ID <span className="text-destructive">*</span>
                </Label>
                <Input
                  ref={firstInputRef}
                  type="number"
                  min={1}
                  value={venueIdInput.inputValue}
                  onChange={(e) => {
                    venueIdInput.handleChange(e)
                    const nextRaw = e.target.value
                    let nextValue: NumericInputValue = ""
                    if (nextRaw !== "") {
                      const parsed = Number(nextRaw)
                      nextValue = Number.isNaN(parsed) ? "" : parsed
                    }
                    setFormData((prev) => ({
                      ...prev,
                      venueId: nextValue,
                    }))
                  }}
                  onBlur={venueIdInput.handleBlur}
                  className={`bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    venueIdInput.error ? "border-destructive" : ""
                  }`}
                />
                {venueIdInput.error && <p className="text-xs text-destructive">{venueIdInput.error}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">
                  NEC Match Score Threshold <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  max={10000}
                  step={1}
                  value={necThresholdInput.inputValue}
                  onChange={(e) => {
                    necThresholdInput.handleChange(e)
                    const nextRaw = e.target.value
                    let nextValue: NumericInputValue = ""
                    if (nextRaw !== "") {
                      const parsed = Number(nextRaw)
                      nextValue = Number.isNaN(parsed) ? "" : parsed
                    }
                    setFormData((prev) => ({
                      ...prev,
                      necThreshold: nextValue,
                    }))
                  }}
                  onBlur={necThresholdInput.handleBlur}
                  autoComplete="off"
                  className={`bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    necThresholdInput.error ? "border-destructive" : ""
                  }`}
                />
                {necThresholdInput.error && <p className="text-xs text-destructive">{necThresholdInput.error}</p>}
                <p className="text-xs text-muted-foreground">Enter an integer between 0 and 10000</p>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label className="text-[13px] text-muted-foreground font-medium">User Instructions</Label>
                  <p className="text-xs text-muted-foreground">Enable to show user instructions on devices</p>
                </div>
                <Switch
                  checked={formData.userInstructionsEnabled}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, userInstructionsEnabled: checked }))
                  }
                  className="data-[state=checked]:bg-primary"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">Admin password</Label>
                <Input
                  type="text"
                  value={formData.adminPassword}
                  onChange={(e) => setFormData((prev) => ({ ...prev, adminPassword: e.target.value }))}
                  placeholder="Leave blank to keep current"
                  className="bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">Leave blank to keep current. To change it, enter a new password.</p>
              </div>
            </div>
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
              className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || Object.keys(errors).length > 0}
              className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
            >
              {isSaving && <LoadingSpinner size="sm" className="mr-2" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowConfirmDialog(false)}
              className="bg-secondary border-input text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
            >
              Keep Editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
